import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { ArrowBackIosNew } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import TextButton from "@/components/TextButton";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { useFormatPrice, useLayoutTier } from "@/hooks";
import { useStepNavigation } from "@/hooks/useStepNavigation";
import { primaryActionSx, theme } from "@/theme/mui-theme";
import { formatDateRange, formatGuests, getCampsiteTypeLabel } from "@/utils/booking";
import BookingPriceDetailsContent from "./BookingPriceDetailsContent";
import BookingSearchDetails from "./BookingSearchDetails";
import CampgroundHeader from "./CampgroundHeader";

interface BookingBottomBarProps {
  nextButtonText?: string;
  nextLoading?: boolean;
  nextDisabled?: boolean;
  nextAction?: () => Promise<boolean>;
}

export default function BookingBottomBar({
  nextButtonText,
  nextLoading,
  nextDisabled,
  nextAction,
}: BookingBottomBarProps) {
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { i18n } = useLingui();
  const { goToNextStep, goToPreviousStep, nextStep, previousStep } = useStepNavigation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const tier = useLayoutTier();

  const price = state.price;
  const { formatPrice, formatPriceAlt } = useFormatPrice();

  const handleNext = async () => {
    if (nextAction) {
      if (await nextAction()) {
        goToNextStep();
      }
    } else {
      goToNextStep();
    }
  };

  const headerImage = state.selectedCampsite?.images?.[0];
  const imageUrl = headerImage?.variants?.["4_3"]?.url;

  const campsiteTypeLabel = getCampsiteTypeLabel(
    state.selectedCampsiteType,
    campgroundData.campsite_types,
  );
  const dateRangeLabel = formatDateRange([state.dateFrom, state.dateUntil], i18n.locale);
  const guestsLabel = formatGuests(state.selectedPersons, campgroundData.campground?.persons || []);

  const buttonSx = nextStep === "confirmation" ? primaryActionSx : {};
  const buttonLabel = nextButtonText || t`Next`;

  return (
    <>
      {/* Collapsed bar: full-bleed so it spans edge to edge of the page/
          iframe regardless of ancestor padding, while staying in normal
          document flow (unlike position:fixed, this can't cover the footer
          that renders below it). Uses left+transform rather than margin,
          since the parent Stack's `spacing` prop forces margin:0 on its
          direct children and would silently override a margin-based
          full-bleed trick. */}
      <Stack
        sx={{
          bgcolor: "background.paper",
          p: 2,
          height: 180,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: { xs: "block", sm: "block", md: "none" },
          position: "relative",
          width: "100vw",
          left: "50%",
          transform: "translateX(-50%)",
        }}
        spacing={2}
      >
        {/* Price + details summary */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {formatPrice(price?.total || 0)}
            {price?.currency_alt && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({formatPriceAlt(price.currency_alt.total)})
              </Typography>
            )}
          </Typography>
          <TextButton onClick={() => setDetailsOpen(true)} sx={{ fontWeight: 600 }}>
            <Trans>More details</Trans>
          </TextButton>
        </Stack>

        <Stack>
          <Typography variant="body2" textTransform="capitalize" color="text.secondary">
            {[campsiteTypeLabel, dateRangeLabel].filter(Boolean).join(", ")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {guestsLabel}
          </Typography>
        </Stack>

        {/* Navigation buttons */}
        <Stack direction="row" spacing={2} alignItems="center">
          {previousStep && (
            <IconButton
              onClick={goToPreviousStep}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                width: 60,
                height: 60,
                flexShrink: 0,
              }}
            >
              <ArrowBackIosNew sx={{ color: "text.primary" }} />
            </IconButton>
          )}
          <Button
            fullWidth
            variant="contained"
            size="large"
            loading={nextLoading}
            onClick={handleNext}
            disabled={nextDisabled}
            sx={{ height: 60, ...buttonSx }}
          >
            {buttonLabel}
          </Button>
        </Stack>
      </Stack>

      {/* Mobile: expanded details drawer */}
      <Drawer
        anchor="bottom"
        open={tier === "mobile" && detailsOpen}
        onClose={() => setDetailsOpen(false)}
        slotProps={{
          paper: {
            sx: {
              height: "90vh",
              borderRadius: "20px 20px 0 0",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            {imageUrl && (
              <Box sx={{ width: "100%", height: 180, overflow: "hidden" }}>
                <img
                  src={imageUrl}
                  alt={headerImage?.title || ""}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            )}
            <IconButton
              onClick={() => setDetailsOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0, 0, 0, 0.4)",
                color: "white",
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.6)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", px: 3, pt: 3, pb: 2 }}>
            <Stack spacing={2.5} divider={<Divider />}>
              <CampgroundHeader variant="compact" />
              <BookingSearchDetails variant="compact" />
              <BookingPriceDetailsContent />
            </Stack>
          </Box>

          <Box sx={{ px: 3, pb: 3, pt: 2, flexShrink: 0 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              loading={nextLoading}
              onClick={() => {
                setDetailsOpen(false);
                handleNext();
              }}
              disabled={nextDisabled}
              sx={buttonSx}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Tablet: price details dialog */}
      <Dialog
        open={tier === "tablet" && detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 5 } } }}
      >
        <DialogTitle>
          <Trans>Price Details</Trans>
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BookingPriceDetailsContent showTitle={false} />
        </DialogContent>
      </Dialog>
    </>
  );
}
